import React, { useState, useMemo } from "react";
import { Table, Tag, Avatar, Button, Space, Dropdown } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { translations } from "../../../utils/Translation/staticTranslation";

const StoreTable = ({ merchantStoreData }) => {
  const lang = localStorage.getItem("language") || "ar";
  const t = translations[lang]?.MerchantAnalytics || {};
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
  });

  const avatarColors = [
    "#E0F7FA",
    "#FFF3E0",
    "#E8F5E9",
    "#EDE7F6",
    "#F3E5F5",
    "#FCE4EC",
    "#E1F5FE",
  ];

  const dataSource = useMemo(() => {
    if (!merchantStoreData?.result?.top_5) return [];

    return merchantStoreData.result.top_5.map((store, index) => {
      const randomColor =
        avatarColors[index % avatarColors.length] || "#F3F4F6";
      const textColor = "#111";

      return {
        key: index + 1,
        store: store.name || "N/A",
        merchant: store.merchant_name || "N/A",
        email: store.email || "N/A",
        views: store.clicks?.toLocaleString() || "0",
        impressions: store.impressions?.toLocaleString() || "0",
        products: store.products?.toLocaleString() || "0",
        country: store.country?.[0] || "N/A",
        flag: store.country?.[0]?.toLowerCase() || "sa",
        status: store.active
          ? t.tables?.active || "Active"
          : t.tables?.inactive || "Inactive",
        profile: store.profile || null,
        avatarBg: randomColor,
        avatarTextColor: textColor,
      };
    });
  }, [merchantStoreData]);

  const columns = [
    {
      title: t.tables?.store || "Store",
      dataIndex: "store",
      key: "store",
      render: (text, record) => (
        <Space>
          <Avatar
            style={{
              backgroundColor: record.avatarBg,
              color: record.avatarTextColor,
              fontWeight: 600,
              fontSize: "14px",
            }}
            size={38}
          >
            {record.store?.substring(0, 2).toUpperCase()}
          </Avatar>
          <span style={{ fontWeight: 500 }}>{text}</span>
        </Space>
      ),
    },
    {
      title: t.tables?.merchants || "Merchant",
      dataIndex: "merchant",
      key: "merchant",
      render: (text, record) => (
        <Space>
          <Avatar
            src={
              record.profile ||
              `https://ui-avatars.com/api/?name=${encodeURIComponent(
                record.merchant
              )}&background=random`
            }
            style={
              !record.profile
                ? {
                    backgroundColor: record.avatarBg,
                    color: "#111",
                    fontWeight: 600,
                  }
                : {}
            }
          >
            {!record.profile && record.merchant?.substring(0, 2).toUpperCase()}
          </Avatar>
          <div>
            <div style={{ fontWeight: 500 }}>{text}</div>
            <div style={{ color: "#8c8c8c", fontSize: 12 }}>{record.email}</div>
          </div>
        </Space>
      ),
    },
    { title: t.tables?.clicks || "Clicks", dataIndex: "views", key: "views" },
    {
      title: t.tables?.impressions || "Impressions",
      dataIndex: "impressions",
      key: "impressions",
    },
    {
      title: t.tables?.products || "Products",
      dataIndex: "products",
      key: "products",
    },
    {
      title: t.tables?.country || "Country",
      dataIndex: "country",
      key: "country",
      render: (_, record) => (
        <Space>
          <img
            src={`https://flagcdn.com/w40/${record.flag}.png`}
            alt={record.country}
            style={{ width: 22, borderRadius: "50%" }}
            onError={(e) => (e.target.style.display = "none")}
          />
          {record.country}
        </Space>
      ),
    },
    {
      title: t.tables?.status || "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let color = "#101828";
        let background = "#F9FAFB";
        let border = "#E5E7EB";

        if (status === (t.tables?.active || "Active")) {
          color = "#027A48";
          background = "#ECFDF3";
          border = "#A4F4CF";
        } else if (status === (t.tables?.pending || "Pending")) {
          color = "#B54708";
          background = "#FFFAEB";
          border = "#FCD9BD";
        } else if (status === (t.tables?.waiting || "Waiting")) {
          color = "#155EEF";
          background = "#EFF8FF";
          border = "#BEDBFF";
        }

        return (
          <Tag
            style={{
              color,
              backgroundColor: background,
              border: `1px solid ${border}`,
              borderRadius: "8px",
              padding: "2px 10px",
              fontSize: "13px",
              fontWeight: 500,
            }}
          >
            {status}
          </Tag>
        );
      },
    },
  ];

  return (
    <div style={{ background: "#f9fafb" }}>
      {/* Header Section */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <h2 style={{ fontSize: 20, fontWeight: 600, color: "#000" }}>
          {t.tables?.top_stores || "Top Stores"}
        </h2>
      </div>

      {/* Table Section */}
      <div
        style={{
          background: "white",
          borderRadius: "12px",
          overflow: "hidden", // ✅ keeps corners smooth
          borderTop: "1px solid #E5E7EB", // ✅ Added top border
          boxShadow: "0 1px 4px #E5E7EB",
        }}
      >
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          style={{
            borderRadius: "12px",
          }}
        />
      </div>
    </div>
  );
};

export default StoreTable;
